// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject, ContextSuppliers } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import { Box, Typography } from '@mui/material';
import { DialogActions } from '../../../dialogs/DialogActions/DialogActions';

// icons
import {
	Add as IconAdd,
	Assessment as IconAssessment,
	AttachMoney as IconMoney,
	Description as IconContract,
	Download as IconDownload,
	Link as IconLink,
	Payment as IconPayment,
	TrendingUp as IconTrending,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// components
import { ContractsDataTable } from './ContractsDataTable';

// dialogs
import { DialogAddContract } from '../../../dialogs/DialogAddContract/DialogAddContract';
import { DialogPrepareForPayment } from '../../../dialogs/DialogPrepareForPayment/DialogPrepareForPayment';
import {
	DialogBulkUpload,
	v2ContractSchema,
	downloadTemplate,
	schemaWithCustomFields,
	LookupData,
} from '../../../dialogs/DialogBulkUpload';

// data
import { COLORS } from '../../../../data';

// v2 components
import { CustomFieldsLink } from '../../../ui/CustomFieldsLink/CustomFieldsLink';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { StatCardRow } from '../../../ui/StatCardRow/StatCardRow';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// types
import { V2Contract } from '../../../../@types';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { getProjectLocalCurrency } from '../../../../utils/localCurrency';
import { resolveContractFxRate } from '../../../../utils/fxRate';
import {
	getContractAmountPaid,
	getContractAmountPaidV1,
	getContractCommitmentAmount,
	getContractNum,
	getContractPaymentProgressPct,
} from './Contracts.utils';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

export const Contracts = () => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const {
		contracts,
		deactivateContract,
		deleteContract,
		workPlans,
		loading,
		pagination,
		setFilterQuery,
		refetchActivities,
		refetchContracts,
	} = useContext(ContextSpendPlan);
	const { refetchSuppliers } = useContext(ContextSuppliers);

	// Delete-confirmation dialog state for the row-action delete.
	const [contractPendingDelete, setContractPendingDelete] = useState<V2Contract | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Hard-delete (DRAFT + unlinked) state, kept separate so the dialog copy
	// and the API call stay distinct from the deactivate path above.
	const [contractPendingHardDelete, setContractPendingHardDelete] = useState<V2Contract | null>(
		null
	);
	const [isHardDeleting, setIsHardDeleting] = useState(false);

	// Ensure the listing reflects latest server state when the user returns
	// from detail pages where status updates can happen outside this context.
	useEffect(() => {
		refetchContracts();
	}, [refetchContracts]);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogContract = searchParams.get('dialogContract');
	const filterWorkPlan = searchParams.get('workPlan');
	const filterStatus = searchParams.get('status') || 'ALL';
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'created';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';
	const searchQuery = searchParams.get('search') || '';

	// Sync URL params with context filter query
	useEffect(() => {
		setFilterQuery('contracts', {
			...(filterWorkPlan && { workPlan: parseInt(filterWorkPlan) }),
			...(filterStatus !== 'ALL' && { status: filterStatus }),
			...(searchQuery && { search: searchQuery }),
			limit: parseInt(filterLimit),
			order: filterOrder,
			orderBy: filterOrderBy,
			page: parseInt(filterPage),
		});
	}, [
		filterWorkPlan,
		filterStatus,
		filterPage,
		filterLimit,
		filterOrderBy,
		filterOrder,
		searchQuery,
		setFilterQuery,
	]);

	// All contracts from context (server-side filtered)
	const allContracts = contracts;

	// Lookup data for bulk upload, fetched as the full project universe (no page
	// limit) so the resolve-relationship dropdowns list every activity and
	// supplier for the project, not just the current listing page.
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: paramDialog === 'bulkUpload',
		activities: true,
		suppliers: true,
	});
	const lookupData: LookupData = useMemo(
		() => ({
			activities: lookups.activities,
			suppliers: lookups.suppliers,
		}),
		[lookups.activities, lookups.suppliers]
	);

	// handlers for search/filters
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			params.set('page', '1');
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			if (key === 'status') {
				if (value === 'ALL') {
					params.delete('status');
				} else {
					params.set('status', String(value));
				}
			} else if (key === 'workPlan') {
				if (value === 'ALL') {
					params.delete('workPlan');
				} else {
					params.set('workPlan', String(value));
				}
			}
			params.set('page', '1');
			return params;
		});
	};

	// handlers for pagination and sorting
	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (column: string, direction: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', column);
			params.set('order', direction);
			return params;
		});
	};

	// Dialog handlers
	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogActivity');
			params.delete('dialogContract');
			return params;
		});
	};

	const handleOpenAddContractDialog = () => {
		searchParams.set('dialog', 'addContract');
		setSearchParams(searchParams);
	};

	const handleOpenLinkDialog = () => {
		searchParams.set('dialog', 'linkContract');
		setSearchParams(searchParams);
	};

	const handleBulkUploadFile = () => {
		searchParams.set('dialog', 'bulkUpload');
		setSearchParams(searchParams);
	};

	const handleDownloadTemplate = async () => {
		try {
			await downloadTemplate({
				schema: schemaWithCustomFields(v2ContractSchema, project?.customFieldDefinitions ?? []),
				lookupData,
			});
			setBanner({ message: 'Template downloaded', severity: 'success' });
		} catch {
			setBanner({ message: 'Failed to generate template', severity: 'error' });
		}
	};

	const handleAddContractSuccess = () => {
		// On V2 projects, FormManageContractV2 internally handles the optional
		// activity link via PATCH /v2/contracts/:id after creating the legal
		// contract row. Either way we just refetch — the contract is now
		// visible in the listing even when no activity was assigned (see
		// story #112: v_contract view + GET handler now return unassigned
		// contracts via contract.project_id fallback).
		refetchContracts();
	};

	// Contract row actions
	const handlePreparePayment = (contract: V2Contract) => {
		setSearchParams((params) => {
			params.set('dialog', 'preparePayment');
			params.set('dialogContract', String(contract.id));
			return params;
		});
	};

	const handleEditContract = (contract: V2Contract) => {
		setSearchParams((params) => {
			params.set('dialog', 'editContract');
			params.set('dialogContract', String(contract.id));
			return params;
		});
	};

	const handleDeleteContract = async (contract: V2Contract) => {
		setContractPendingDelete(contract);
	};

	const handleDeleteDraftContract = (contract: V2Contract) => {
		setContractPendingHardDelete(contract);
	};

	const handleConfirmDelete = async () => {
		if (!contractPendingDelete) return;
		setIsDeleting(true);
		const response = await deactivateContract(contractPendingDelete.id);
		setIsDeleting(false);
		setContractPendingDelete(null);

		if (response.status === 200 || response.status === 204) {
			setBanner({
				message: `Contract deactivated successfully.`,
				severity: 'success',
			});
		} else if (response.error) {
			setBanner({
				message: `Failed to deactivate contract: ${response.error.message}`,
				severity: 'error',
			});
		}
	};

	const handleConfirmHardDelete = async () => {
		if (!contractPendingHardDelete) return;
		setIsHardDeleting(true);
		const response = await deleteContract(contractPendingHardDelete.id);
		setIsHardDeleting(false);
		const label = getContractNum(contractPendingHardDelete);
		setContractPendingHardDelete(null);

		if (response.status === 200 || response.status === 204) {
			setBanner({
				message: `Contract "${label}" deleted.`,
				severity: 'success',
			});
		} else if (response.error) {
			// Surface the backend guard hint verbatim so the PIU knows why
			// delete was refused (e.g. linked vouchers, conditions, etc.).
			const hint = (response.error as { details?: { hint?: string } }).details?.hint ?? '';
			setBanner({
				message: `Failed to delete contract: ${response.error.message}${hint ? ` ${hint}` : ''}`,
				severity: 'error',
			});
		}
	};

	// Story #112: open the link dialog for an Unassigned contract. The
	// DialogLinkContract is mounted at the ProjectV2 level and listens for
	// `dialog=linkContract&dialogContract=<id>` URL params.
	const handleLinkContract = (contract: V2Contract) => {
		setSearchParams((params) => {
			params.set('dialog', 'linkContract');
			params.set('dialogContract', String(contract.id));
			return params;
		});
	};

	// Summary stats
	const summaryStats = useMemo(() => {
		const draftContracts = allContracts.filter((c) => c.status === 'DRAFT');
		const submittedContracts = allContracts.filter((c) => c.status === 'SUBMITTED');
		const activeContracts = allContracts.filter((c) => c.status === 'ACTIVE');
		const completedContracts = allContracts.filter((c) => c.status === 'COMPLETED');

		const totalCommitment = allContracts.reduce(
			(sum, c) =>
				sum +
				(getContractCommitmentAmount(
					c,
					workPlans.find((e) => e.id === c.workPlan)
				) ?? 0),
			0
		);

		const totalPaid = allContracts.reduce(
			(sum, c) => sum + getContractAmountPaid(c) + getContractAmountPaidV1(c),
			0
		);

		// Local-currency equivalents. Each contract restates its reporting
		// figures through the canonical resolver (commitment scalars first,
		// then its multi-currency amount rows, then the work plan as a
		// last-resort fallback) so totals rest on a consistent basis.
		const projectLocalCurrency = getProjectLocalCurrency(project);
		const contractLocalRate = (c: (typeof allContracts)[number]): number | null => {
			const workPlan = workPlans.find((e) => e.id === c.workPlan);
			return resolveContractFxRate(c, workPlan, project)?.rate ?? null;
		};
		let hasAnyLocal = false;
		const totalCommitmentLocal = allContracts.reduce((sum, c) => {
			const rate = contractLocalRate(c);
			if (rate == null) return sum;
			hasAnyLocal = true;
			const reporting =
				getContractCommitmentAmount(
					c,
					workPlans.find((e) => e.id === c.workPlan)
				) ?? 0;
			return sum + reporting * rate;
		}, 0);
		const totalPaidLocal = allContracts.reduce((sum, c) => {
			const rate = contractLocalRate(c);
			if (rate == null) return sum;
			return sum + (getContractAmountPaid(c) + getContractAmountPaidV1(c)) * rate;
		}, 0);

		const totalMilestones = allContracts.reduce((sum, c) => sum + c.totalPaymentMilestones, 0);
		const completedMilestones = allContracts.reduce((sum, c) => sum + c.milestonesCompleted, 0);

		// Contracts with verified conditions ready for payment
		const readyForPayment = allContracts.filter(
			(c) => c.milestonesCompleted < c.totalPaymentMilestones && c.status === 'ACTIVE'
		).length;

		// Average payment progress
		const avgProgress =
			allContracts.length > 0
				? Math.round(
						allContracts.reduce((sum, c) => sum + getContractPaymentProgressPct(c), 0) /
							allContracts.length
					)
				: 0;

		// Primary currency (from first work plan, first contract, or USD)
		const primaryCurrency =
			workPlans[0]?.currency || allContracts[0]?.amounts?.[0]?.currency || 'USD';

		// Story #112: unassigned contracts have no activityId. Surface the
		// count so users can find and complete them.
		const unassignedCount = allContracts.filter((c) => !c.planningActivity).length;

		return {
			activeCount: activeContracts.length,
			avgProgress,
			completedCount: completedContracts.length,
			draftCount: draftContracts.length,
			submittedCount: submittedContracts.length,
			completedMilestones,
			primaryCurrency,
			projectLocalCurrency,
			readyForPayment,
			totalCommitment,
			totalCommitmentLocal: hasAnyLocal ? totalCommitmentLocal : null,
			totalCount: allContracts.length,
			totalMilestones,
			totalPaid,
			totalPaidLocal: hasAnyLocal ? totalPaidLocal : null,
			totalRemaining: totalCommitment - totalPaid,
			unassignedCount,
		};
	}, [allContracts, workPlans, project]);

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconContract}
				title={t('contracts')}
				subtitle={t('spendPlan.contracts.subtitle')}
				variant="contract"
				secondaryBadge={<CustomFieldsLink entity="contract" />}
				actions={
					<BtnSplit
						actions={[
							{
								children: t('addContract'),
								onClick: handleOpenAddContractDialog,
								startIcon: <IconAdd />,
							},
							{
								children: t('spendPlan.linkContract'),
								onClick: handleOpenLinkDialog,
								startIcon: <IconLink />,
							},
							{
								children: t('spendPlan.bulkUploadFromFile'),
								onClick: handleBulkUploadFile,
								startIcon: <IconUpload />,
							},
							{
								children: t('spendPlan.downloadTemplate'),
								onClick: handleDownloadTemplate,
								startIcon: <IconDownload />,
							},
						]}
					/>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Summary Metrics Cards */}
				<StatCardRow
					loading={loading.contracts}
					cards={[
						{
							icon: IconMoney,
							label: t('spendPlan.contracts.totalCommitment'),
							value: formatCurrency({
								currency: summaryStats.primaryCurrency,
								value: summaryStats.totalCommitment,
							}),
							valueLocal:
								summaryStats.projectLocalCurrency && summaryStats.totalCommitmentLocal != null
									? formatCurrency({
											currency: summaryStats.projectLocalCurrency,
											value: summaryStats.totalCommitmentLocal,
										})
									: undefined,
							secondary: t('spendPlan.contracts.contractCount', { count: summaryStats.totalCount }),
							color: COLORS.primary.main,
						},
						{
							icon: IconPayment,
							label: t('spendPlan.contracts.totalPaid'),
							value: formatCurrency({
								currency: summaryStats.primaryCurrency,
								value: summaryStats.totalPaid,
							}),
							valueLocal:
								summaryStats.projectLocalCurrency && summaryStats.totalPaidLocal != null
									? formatCurrency({
											currency: summaryStats.projectLocalCurrency,
											value: summaryStats.totalPaidLocal,
										})
									: undefined,
							secondary: t('spendPlan.contracts.avgProgress', { pct: summaryStats.avgProgress }),
							color: COLORS.success.main,
						},
						{
							icon: IconAssessment,
							label: t('spendPlan.contracts.contractStatus'),
							value: t('spendPlan.contracts.activeCount', { count: summaryStats.activeCount }),
							secondary:
								summaryStats.unassignedCount > 0
									? t('spendPlan.contracts.doneUnassigned', {
											done: summaryStats.completedCount,
											unassigned: summaryStats.unassignedCount,
										})
									: `${summaryStats.completedCount} done`,
							color: COLORS.info.main,
						},
						{
							icon: IconTrending,
							label: t('spendPlan.contracts.paymentMilestones'),
							value: `${summaryStats.completedMilestones}/${summaryStats.totalMilestones}`,
							secondary: t('spendPlan.contracts.pctComplete', {
								pct:
									summaryStats.totalMilestones > 0
										? Math.round(
												(summaryStats.completedMilestones / summaryStats.totalMilestones) * 100
											)
										: 0,
							}),
							color: COLORS.warning.main,
						},
					]}
				/>

				{/* Contracts DataTable */}
				<ContractsDataTable
					isLoading={loading.contracts}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					orderBy={filterOrderBy}
					order={filterOrder}
					onSortChange={handleSortChange}
					page={parseInt(filterPage)}
					rowsPerPage={parseInt(filterLimit)}
					totalCount={pagination.contracts?.total || 0}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					filterValues={{
						workPlan: filterWorkPlan || 'ALL',
						status: filterStatus,
					}}
					onFilterChange={handleFilterChange}
					onPreparePayment={handlePreparePayment}
					onEditContract={handleEditContract}
					onDeleteContract={handleDeleteContract}
					onDeleteDraftContract={handleDeleteDraftContract}
					onLinkContract={handleLinkContract}
				/>
			</Box>

			{/* Add Contract Dialog */}
			<DialogAddContract
				isOpen={paramDialog === 'addContract'}
				onClose={handleCloseDialog}
				onSuccess={handleAddContractSuccess}
			/>

			{/* Prepare for Payment Dialog */}
			<DialogPrepareForPayment
				contract={
					paramDialog === 'preparePayment' && paramDialogContract
						? contracts.find((c) => c.id === Number(paramDialogContract)) || null
						: null
				}
				isOpen={paramDialog === 'preparePayment'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					handleCloseDialog();
					setBanner({
						message: t('spendPlan.preparePayment.success', 'Payment voucher created successfully'),
						severity: 'success',
					});
				}}
			/>

			{/* Bulk Upload Contracts Dialog */}
			<DialogBulkUpload
				isOpen={paramDialog === 'bulkUpload'}
				onClose={handleCloseDialog}
				schema={v2ContractSchema}
				projectId={project?.id || 0}
				lookupData={lookupData}
				customFieldDefinitions={project?.customFieldDefinitions}
				onLookupDataRefresh={async (contextKey) => {
					if (contextKey === 'activities') {
						await Promise.all([refetchActivities(), lookups.refetch()]);
						return;
					}
					if (contextKey === 'suppliers') {
						await Promise.all([refetchSuppliers(), lookups.refetch()]);
					}
				}}
				onSuccess={() => {
					refetchContracts();
					setBanner({ message: 'Contract links uploaded successfully', severity: 'success' });
				}}
			/>

			{/* Deactivate Contract Confirmation */}
			<DialogActions
				isOpen={contractPendingDelete !== null}
				onClose={() => !isDeleting && setContractPendingDelete(null)}
				maxWidth={480}
				title={{ children: 'Deactivate Contract' }}
				subtitle={{
					children: `Deactivate "${contractPendingDelete ? getContractNum(contractPendingDelete) : ''}"?`,
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isDeleting,
						onClick: () => setContractPendingDelete(null),
						variant: 'text',
					},
					{
						children: isDeleting ? 'Deactivating...' : 'Deactivate Contract',
						color: 'error',
						disabled: isDeleting,
						loading: isDeleting,
						onClick: handleConfirmDelete,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					This will remove the contract from the spend plan. Deactivation is blocked if any payments
					have been made or are in flight against this contract. This action cannot be undone.
				</Typography>
			</DialogActions>

			{/* Delete Draft Contract Confirmation */}
			<DialogActions
				isOpen={contractPendingHardDelete !== null}
				onClose={() => !isHardDeleting && setContractPendingHardDelete(null)}
				maxWidth={480}
				title={{ children: 'Delete Draft Contract' }}
				subtitle={{
					children: `Delete "${contractPendingHardDelete ? getContractNum(contractPendingHardDelete) : ''}"?`,
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						disabled: isHardDeleting,
						onClick: () => setContractPendingHardDelete(null),
						variant: 'text',
					},
					{
						children: isHardDeleting ? 'Deleting...' : 'Delete Contract',
						color: 'error',
						disabled: isHardDeleting,
						loading: isHardDeleting,
						onClick: handleConfirmHardDelete,
						variant: 'contained',
					},
				]}>
				<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
					This permanently removes the draft contract. Delete is only allowed while the contract is
					still in Draft and has no conditions, invoices, allocations, or vouchers attached. Remove
					any of those first &mdash; they are not deleted automatically. This action is final and
					cannot be undone.
				</Typography>
			</DialogActions>
		</Box>
	);
};
